import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import {
    TrendingUp,
    TrendingDown,
    Minus,
    Users,
    PlusCircle,
    MinusCircle,
    Percent,
    DollarSign,
    Activity,
} from 'lucide-react'
import { ServiceMonthlyReportItem } from '../types'
import { calculateGrowthRate, calculateChange } from '../services'

interface SummaryCardsProps {
    data: ServiceMonthlyReportItem[]
    previousYearData?: ServiceMonthlyReportItem[]
    showComparison: boolean
}

interface StatCardProps {
    title: string
    value: string | number
    previousValue?: string | number
    growth?: number | null
    isPercentChange?: boolean
    isNegativeGood?: boolean
    icon: React.ReactNode
    colorClass: string
}

const StatCard: React.FC<StatCardProps> = ({
    title,
    value,
    previousValue,
    growth,
    isPercentChange = false,
    isNegativeGood = false,
    icon,
    colorClass,
}) => {
    const { t } = useTranslation()

    const renderGrowthIndicator = () => {
        if (growth === null || growth === undefined) return null

        const displayValue = isPercentChange
            ? `${growth >= 0 ? '+' : ''}${growth.toFixed(2)}%`
            : `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}%`

        if (growth > 0) {
            return (
                <div
                    className={`flex items-center gap-0.5 ${isNegativeGood ? 'text-red-600' : 'text-green-600'}`}
                >
                    <TrendingUp size={14} />
                    <span className="text-xs font-medium">{displayValue}</span>
                </div>
            )
        } else if (growth < 0) {
            return (
                <div
                    className={`flex items-center gap-0.5 ${isNegativeGood ? 'text-green-600' : 'text-red-600'}`}
                >
                    <TrendingDown size={14} />
                    <span className="text-xs font-medium">{displayValue}</span>
                </div>
            )
        }
        return (
            <div className="flex items-center gap-0.5 text-gray-500">
                <Minus size={14} />
                <span className="text-xs font-medium">0%</span>
            </div>
        )
    }

    return (
        <div className="bg-white rounded-md p-4 shadow-sm border border-gray-100">
            <div className="flex items-start justify-between mb-2">
                <div className={`p-2 rounded-md ${colorClass}`}>{icon}</div>
                {renderGrowthIndicator()}
            </div>
            <Typogaphy className="text-2xl font-bold text-gray-900">
                {value}
            </Typogaphy>
            <Typogaphy className="text-sm text-gray-500">{title}</Typogaphy>
            {previousValue !== undefined && (
                <Typogaphy className="text-xs text-gray-400 mt-1">
                    {t('previous')}: {previousValue}
                </Typogaphy>
            )}
        </div>
    )
}

const SummaryCards: React.FC<SummaryCardsProps> = ({
    data,
    previousYearData,
    showComparison,
}) => {
    const { t } = useTranslation()

    const summary = useMemo(() => {
        if (data.length === 0)
            return {
                totalNew: 0,
                totalCancelled: 0,
                totalCancelledCustomers: 0,
                totalCancelledAmount: 0,
                avgCancellationRate: 0,
                avgRetentionRate: 0,
                netChange: 0,
                startingServices: 0,
                endingServices: 0,
            }

        const totalNew = data.reduce((sum, d) => sum + d.new_services, 0)
        const totalCancelled = data.reduce(
            (sum, d) => sum + d.cancelled_services,
            0
        )
        const totalCancelledCustomers = data.reduce(
            (sum, d) => sum + d.cancelled_customers,
            0
        )
        const totalCancelledAmount = data.reduce(
            (sum, d) => sum + d.cancelled_amount,
            0
        )
        const avgCancellationRate =
            data.reduce((sum, d) => sum + d.cancellation_rate, 0) / data.length
        const avgRetentionRate =
            data.reduce((sum, d) => sum + d.retention_rate, 0) / data.length

        return {
            totalNew,
            totalCancelled,
            totalCancelledCustomers,
            totalCancelledAmount,
            avgCancellationRate,
            avgRetentionRate,
            netChange: totalNew - totalCancelled,
            startingServices: data[0]?.starting_services || 0,
            endingServices: data[data.length - 1]?.ending_services || 0,
        }
    }, [data])

    const prevSummary = useMemo(() => {
        if (!previousYearData || previousYearData.length === 0) return null

        const totalNew = previousYearData.reduce(
            (sum, d) => sum + d.new_services,
            0
        )
        const totalCancelled = previousYearData.reduce(
            (sum, d) => sum + d.cancelled_services,
            0
        )
        const totalCancelledCustomers = previousYearData.reduce(
            (sum, d) => sum + d.cancelled_customers,
            0
        )
        const totalCancelledAmount = previousYearData.reduce(
            (sum, d) => sum + d.cancelled_amount,
            0
        )
        const avgCancellationRate =
            previousYearData.reduce((sum, d) => sum + d.cancellation_rate, 0) /
            previousYearData.length
        const avgRetentionRate =
            previousYearData.reduce((sum, d) => sum + d.retention_rate, 0) /
            previousYearData.length

        return {
            totalNew,
            totalCancelled,
            totalCancelledCustomers,
            totalCancelledAmount,
            avgCancellationRate,
            avgRetentionRate,
            netChange: totalNew - totalCancelled,
            startingServices: previousYearData[0]?.starting_services || 0,
            endingServices:
                previousYearData[previousYearData.length - 1]
                    ?.ending_services || 0,
        }
    }, [previousYearData])

    const formatCurrency = (amount: number) => {
        if (amount >= 1000000000) {
            return `${(amount / 1000000000).toFixed(1)}B`
        }
        if (amount >= 1000000) {
            return `${(amount / 1000000).toFixed(1)}M`
        }
        if (amount >= 1000) {
            return `${(amount / 1000).toFixed(1)}K`
        }
        return amount.toLocaleString()
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {/* Ending Services */}
            <StatCard
                title={t('ending_services')}
                value={summary.endingServices.toLocaleString()}
                previousValue={
                    showComparison && prevSummary
                        ? prevSummary.endingServices.toLocaleString()
                        : undefined
                }
                growth={
                    showComparison && prevSummary
                        ? calculateGrowthRate(
                              summary.endingServices,
                              prevSummary.endingServices
                          )
                        : undefined
                }
                icon={<Activity size={20} className="text-blue-600" />}
                colorClass="bg-blue-100"
            />

            {/* New Services */}
            <StatCard
                title={t('new_services')}
                value={summary.totalNew.toLocaleString()}
                previousValue={
                    showComparison && prevSummary
                        ? prevSummary.totalNew.toLocaleString()
                        : undefined
                }
                growth={
                    showComparison && prevSummary
                        ? calculateGrowthRate(
                              summary.totalNew,
                              prevSummary.totalNew
                          )
                        : undefined
                }
                icon={<PlusCircle size={20} className="text-green-600" />}
                colorClass="bg-green-100"
            />

            {/* Cancelled Services */}
            <StatCard
                title={t('cancelled_services')}
                value={summary.totalCancelled.toLocaleString()}
                previousValue={
                    showComparison && prevSummary
                        ? prevSummary.totalCancelled.toLocaleString()
                        : undefined
                }
                growth={
                    showComparison && prevSummary
                        ? calculateGrowthRate(
                              summary.totalCancelled,
                              prevSummary.totalCancelled
                          )
                        : undefined
                }
                isNegativeGood={true}
                icon={<MinusCircle size={20} className="text-red-600" />}
                colorClass="bg-red-100"
            />

            {/* Cancelled Customers */}
            <StatCard
                title={t('cancelled_customers')}
                value={summary.totalCancelledCustomers.toLocaleString()}
                previousValue={
                    showComparison && prevSummary
                        ? prevSummary.totalCancelledCustomers.toLocaleString()
                        : undefined
                }
                growth={
                    showComparison && prevSummary
                        ? calculateGrowthRate(
                              summary.totalCancelledCustomers,
                              prevSummary.totalCancelledCustomers
                          )
                        : undefined
                }
                isNegativeGood={true}
                icon={<Users size={20} className="text-orange-600" />}
                colorClass="bg-orange-100"
            />

            {/* Cancelled Amount */}
            <StatCard
                title={t('cancelled_amount')}
                value={formatCurrency(summary.totalCancelledAmount)}
                previousValue={
                    showComparison && prevSummary
                        ? formatCurrency(prevSummary.totalCancelledAmount)
                        : undefined
                }
                growth={
                    showComparison && prevSummary
                        ? calculateGrowthRate(
                              summary.totalCancelledAmount,
                              prevSummary.totalCancelledAmount
                          )
                        : undefined
                }
                isNegativeGood={true}
                icon={<DollarSign size={20} className="text-purple-600" />}
                colorClass="bg-purple-100"
            />

            {/* Average Cancellation Rate */}
            <StatCard
                title={t('avg_cancellation_rate')}
                value={`${summary.avgCancellationRate.toFixed(2)}%`}
                previousValue={
                    showComparison && prevSummary
                        ? `${prevSummary.avgCancellationRate.toFixed(2)}%`
                        : undefined
                }
                growth={
                    showComparison && prevSummary
                        ? calculateChange(
                              summary.avgCancellationRate,
                              prevSummary.avgCancellationRate
                          )
                        : undefined
                }
                isPercentChange={true}
                isNegativeGood={true}
                icon={<Percent size={20} className="text-rose-600" />}
                colorClass="bg-rose-100"
            />
        </div>
    )
}

export default SummaryCards
